import {
  normalizePhoneticText,
  similarityScore,
  transliterateForLanguage,
} from "./transliteration";

export const ADMIN_EMAILS = [
  "virmoduho@gmail.com",
  "obuto.mamur@strathmore.edu",
];
export const ADMIN_ROLE = "admin";
export const PROPERTY_TYPES = [
  "Apartment",
  "House",
  "Commercial Building",
  "Hotel",
  "Office",
  "Shop",
  "Store",
  "Plot",
];
export const LISTING_TYPES = [
  "For Sale",
  "For Rent",
  "For Lease",
  "Short Stay",
];
export const ROOM_OPTIONS = Array.from({ length: 100 }, (_, index) => index + 1);
export const BATHROOM_OPTIONS = Array.from(
  { length: 100 },
  (_, index) => index + 1,
);
export const SPACE_SIZE_PROPERTY_TYPES = ["Plot", "Office", "Shop", "House"];
export const REQUIRED_SPACE_SIZE_PROPERTY_TYPES = ["Plot", "Office", "Shop"];
export const RULELESS_PROPERTY_TYPES = ["Plot", "Shop", "Store"];
export const OPTIONAL_ROOM_AND_BATHROOM_PROPERTY_TYPES = [
  "Plot",
  "Store",
  "Office",
  "Shop",
];
export const OPTIONAL_RULES_AND_UTILITIES_PROPERTY_TYPES = [
  "Plot",
  "Store",
  "Office",
  "Shop",
];
export const SPACE_SIZE_OPTIONS = [
  "Under 100 sqm",
  "100 - 250 sqm",
  "251 - 500 sqm",
  "501 - 1,000 sqm",
  "1,001 - 2,500 sqm",
  "Over 2,500 sqm",
];
export const MAX_PROPERTY_IMAGES = 10;
export const MAX_CAR_IMAGES = 8;
export const SUPPORTED_CURRENCIES = ["USD", "SSP"];
export const USER_ROLE_OPTIONS = [
  "agent",
  "broker",
  "property-manager",
  "admin",
];
export const USER_STATUS_OPTIONS = [
  "pending",
  "active",
  "suspended",
  "rejected",
  "terminated",
];
export const MESSAGE_STATUS_OPTIONS = ["new", "read", "resolved"];
export const CONTACT_REQUEST_STATUS_OPTIONS = ["new", "read", "resolved"];
export const HOTEL_REQUEST_STATUS_OPTIONS = ["new", "read", "resolved"];
export const UTILITY_OPTIONS = [
  { name: "tv", label: "TV" },
  { name: "dstv", label: "DStv" },
  { name: "wifi", label: "Wi-Fi" },
  { name: "airConditioning", label: "Air Conditioning" },
  { name: "security", label: "Security" },
  { name: "backupElectricity", label: "Backup Electricity" },
  { name: "parking", label: "Parking" },
  { name: "swimmingPool", label: "Swimming Pool" },
  { name: "gym", label: "Gym" },
];
export const ROOM_FEATURE_OPTIONS = [
  { name: "kitchen", label: "Kitchen" },
  { name: "diningRoom", label: "Dining Room" },
  { name: "sittingRoom", label: "Sitting Room" },
];

const PROPERTY_TYPE_ALIAS_GROUPS = [
  ["Apartment", "شقة", "ኣፓርታማ", "አፓርታማ", "公寓", "Guri dabaq ah"],
  ["House", "منزل", "ገዛ", "ቤት", "房屋", "Guri"],
  ["Hotel", "فندق", "ሆቴል", "ሆቴል", "酒店", "Hudheel"],
  ["Office", "مكتب", "ቤት ጽሕፈት", "ቢሮ", "办公室", "Xafiis"],
  ["Shop", "محل", "ድኳን", "ዱካን", "商铺", "Dukaan"],
  ["Store", "مستودع", "مኽዘን", "መጋዘን", "仓库", "Bakhaar", "Warehouse / Store"],
  ["Plot", "قطعة أرض", "መሬት", "የመሬት ቦታ", "地块", "Dhul", "Plot of Land"],
];

const LISTING_TYPE_ALIAS_GROUPS = [
  ["For Sale", "للبيع", "ንመሸጣ", "ለሽያጭ", "出售", "Iib"],
  ["For Rent", "للإيجار", "ንክራይ", "ለኪራይ", "出租", "Kiro"],
  ["For Lease", "للإيجار بعقد", "ንሊዝ", "ለሊዝ", "租赁", "Liiis"],
  ["Short Stay", "إقامة قصيرة", "ሓጺር መጽንሒ", "አጭር ቆይታ", "短住", "Joogitaan Gaaban"],
];

function buildCanonicalLookup(groups) {
  return new Map(
    groups.flatMap(([canonical, ...aliases]) =>
      [canonical, ...aliases].map((value) => [String(value || "").trim(), canonical]),
    ),
  );
}

const PROPERTY_TYPE_CANONICAL_LOOKUP = buildCanonicalLookup(
  PROPERTY_TYPE_ALIAS_GROUPS,
);
const LISTING_TYPE_CANONICAL_LOOKUP = buildCanonicalLookup(
  LISTING_TYPE_ALIAS_GROUPS,
);
PROPERTY_TYPE_CANONICAL_LOOKUP.set(
  "Commercial Building",
  "Commercial Building",
);
PROPERTY_TYPE_CANONICAL_LOOKUP.set(
  "commercial building",
  "Commercial Building",
);
PROPERTY_TYPE_CANONICAL_LOOKUP.set(
  "Dhismo Ganacsi",
  "Commercial Building",
);
PROPERTY_TYPE_CANONICAL_LOOKUP.set(
  "dhismo ganacsi",
  "Commercial Building",
);

export function canonicalizePropertyTypeValue(value) {
  const normalizedValue = String(value || "").trim();
  return PROPERTY_TYPE_CANONICAL_LOOKUP.get(normalizedValue) || normalizedValue;
}

export function canonicalizeListingTypeValue(value) {
  const normalizedValue = String(value || "").trim();
  return LISTING_TYPE_CANONICAL_LOOKUP.get(normalizedValue) || normalizedValue;
}

const SEARCH_ALIAS_GROUPS = [
  ["juba", "جوبا", "ጁባ", "jubba", "juubba", "朱巴", "朱巴市"],
  [
    "south sudan",
    "جنوب السودان",
    "ደቡብ ሱዳን",
    "koonfur suudaan",
    "南苏丹",
  ],
  ["for rent", "rent", "rental", "للإيجار", "ንክራይ", "kireysi", "出租"],
  ["for sale", "sale", "للبيع", "ንመሸጣ", "iib", "出售"],
  ["for lease", "lease", "للإيجار بعقد", "ንሊዝ", "kiro heshiis", "租赁"],
  [
    "short stay",
    "hotel",
    "hotel apartment",
    "إقامة قصيرة",
    "فندق",
    "ሓጺር መጽንሒ",
    "ሆቴል",
    "joogitaan gaaban",
    "huteel",
    "短租",
    "酒店",
  ],
  ["apartment", "شقة", "ኣፓርታማ", "guri dabaq", "公寓"],
  ["house", "منزل", "ገዛ", "guri", "房屋"],
  ["office", "مكتب", "ቤት ጽሕፈት", "xafiis", "办公室"],
  ["shop", "محل", "ድኳን", "dukaan", "商铺"],
  ["store", "warehouse", "مستودع", "مخزن", "መኽዘን", "bakhaar", "仓库"],
  ["plot", "land", "قطعة أرض", "መሬት", "dhul", "地块"],
  ["car", "cars", "سيارة", "السيارات", "መኪና", "መካይን", "baabuur", "汽车"],
  ["sedan", "سيدان", "轿车"],
  ["suv", "دفع رباعي", "休旅车"],
  ["pickup", "بيك أب", "皮卡"],
  ["van", "فان", "面包车"],
  ["truck", "شاحنة", "卡车"],
  ["bus", "حافلة", "巴士"],
  ["automatic", "أوتوماتيك", "自动挡"],
  ["manual", "عادي", "手动挡"],
  ["petrol", "بنزين", "汽油"],
  ["diesel", "ديزل", "柴油"],
  ["hybrid", "هجين", "混合动力"],
  ["electric", "كهربائي", "电动"],
];

const SEARCH_ALIAS_LOOKUP = new Map(
  SEARCH_ALIAS_GROUPS.flatMap((group) => {
    const canonical = group[0];
    return group.map((alias) => [alias, canonical]);
  }),
);

const DEFAULT_PROPERTY_IMAGE = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="800" viewBox="0 0 1200 800">
    <defs>
      <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#0f172a" />
        <stop offset="100%" stop-color="#1e293b" />
      </linearGradient>
    </defs>
    <rect width="1200" height="800" fill="url(#g)" />
    <path d="M210 520L420 340L600 470L780 280L990 520V620H210Z" fill="#334155" />
    <path d="M300 610V470L420 380L540 470V610Z" fill="#475569" />
    <path d="M690 610V430L780 350L900 430V610Z" fill="#64748b" />
    <circle cx="970" cy="180" r="54" fill="#f8fafc" fill-opacity="0.9" />
  </svg>`,
)}`;

export function getImageUrl(image) {
  if (typeof image === "string" && image.trim()) {
    return image;
  }

  if (image && typeof image === "object" && typeof image.url === "string") {
    return image.url;
  }

  return DEFAULT_PROPERTY_IMAGE;
}

export function formatPrice(price, currency = "USD", locale = "en") {
  const value = Number(price);

  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 0,
    }).format(Number.isFinite(value) ? value : 0);
  } catch (error) {
    return `${currency || "USD"} ${Number.isFinite(value) ? value.toLocaleString() : "0"}`;
  }
}

function getMissingPriceLabel(locale = "en") {
  const normalizedLocale = String(locale || "").toLowerCase();

  if (normalizedLocale.startsWith("ar")) {
    return "اتصل بالإدارة لمعرفة السعر";
  }

  if (normalizedLocale.startsWith("ti")) {
    return "ንዋጋ ንምፍላጥ ምስ ኣድሚን ተራኸቡ";
  }

  if (normalizedLocale.startsWith("so")) {
    return "La xiriir maamulka qiimaha";
  }

  return "Contact admin for price";
}

const LOCALIZED_DIGIT_MAP = {
  "\u0660": "0",
  "\u0661": "1",
  "\u0662": "2",
  "\u0663": "3",
  "\u0664": "4",
  "\u0665": "5",
  "\u0666": "6",
  "\u0667": "7",
  "\u0668": "8",
  "\u0669": "9",
  "\u06f0": "0",
  "\u06f1": "1",
  "\u06f2": "2",
  "\u06f3": "3",
  "\u06f4": "4",
  "\u06f5": "5",
  "\u06f6": "6",
  "\u06f7": "7",
  "\u06f8": "8",
  "\u06f9": "9",
};

function normalizeLocalizedDigits(value) {
  return Array.from(String(value || ""))
    .map((character) => LOCALIZED_DIGIT_MAP[character] || character)
    .join("");
}

export function parseLocalizedNumber(value) {
  const normalizedValue = normalizeLocalizedDigits(value)
    .replace(/[,\s]/g, "")
    .replace(/\u066b/g, ".")
    .replace(/\u066c/g, "");

  const parsedValue = Number(normalizedValue);
  return Number.isFinite(parsedValue) ? parsedValue : NaN;
}

function getBillingSuffix(listingType, locale = "en") {
  const normalizedLocale = String(locale || "").toLowerCase();

  if (listingType === "For Sale") {
    return "";
  }

  if (normalizedLocale.startsWith("ar")) {
    return listingType === "Short Stay" ? "/يوم" : "/شهر";
  }

  if (normalizedLocale.startsWith("ti")) {
    return listingType === "Short Stay" ? "/መዓልቲ" : "/ወርሒ";
  }

  if (normalizedLocale.startsWith("so")) {
    return listingType === "Short Stay" ? "/maalin" : "/bil";
  }

  return listingType === "Short Stay" ? "/DAY" : "/MONTH";
}

export function hasDisplayPrice(price) {
  const value = Number(price);
  return Number.isFinite(value) && value > 0;
}

export function formatPriceWithBilling(
  price,
  listingType,
  currency = "USD",
  locale = "en",
) {
  if (!hasDisplayPrice(price)) {
    return getMissingPriceLabel(locale);
  }

  if (listingType === "For Sale") {
    return formatPrice(price, currency, locale);
  }

  return `${formatPrice(price, currency, locale)}${getBillingSuffix(
    listingType,
    locale,
  )}`;
}

export function formatPropertyTypeLabel(type) {
  const canonicalType = canonicalizePropertyTypeValue(type);

  if (canonicalType === "Plot") {
    return "Plot of Land";
  }

  if (canonicalType === "Store") {
    return "Warehouse / Store";
  }

  return canonicalType || "";
}

export function formatUtilityLabel(key) {
  if (!key) {
    return "";
  }

  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (char) => char.toUpperCase());
}

export function formatRoleLabel(role) {
  return (role || "")
    .split("-")
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ");
}

export function hasAdminAccess(profile, email = "") {
  const normalizedEmail = String(email || "")
    .trim()
    .toLowerCase();

  return (
    normalizeRole(profile?.role) === "admin" ||
    ADMIN_EMAILS.includes(normalizedEmail)
  );
}

export function normalizeRole(role) {
  const normalizedRole = String(role || "")
    .trim()
    .toLowerCase();

  return normalizedRole && normalizedRole !== "pending"
    ? normalizedRole
    : "agent";
}

export function hasAgentAccess(profile, user) {
  const assignedRole = String(profile?.role || "")
    .trim()
    .toLowerCase();
  const accountStatus = String(profile?.accountStatus || "")
    .trim()
    .toLowerCase();

  if (["suspended", "rejected", "terminated"].includes(accountStatus)) {
    return false;
  }

  return (
    ["agent", "broker", "property-manager"].includes(assignedRole) &&
    user?.emailVerified === true
  );
}

export function formatTimestamp(timestamp) {
  if (!timestamp) {
    return "";
  }

  const date =
    typeof timestamp.toDate === "function"
      ? timestamp.toDate()
      : timestamp.seconds
        ? new Date(timestamp.seconds * 1000)
        : new Date(timestamp);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleString();
}

export function normalizeSearchText(value) {
  return normalizePhoneticText(value);
}

function getAliasVariants(value) {
  const normalizedValue = normalizeSearchText(value);

  if (!normalizedValue) {
    return [];
  }

  return SEARCH_ALIAS_GROUPS.filter((group) =>
    group.some((alias) => normalizedValue.includes(normalizeSearchText(alias))),
  ).flatMap((group) => group.map((alias) => normalizeSearchText(alias)));
}

export function buildSearchHaystack(parts) {
  const values = Array.isArray(parts) ? parts : [parts];
  const normalizedValues = values
    .map((value) => normalizeSearchText(value))
    .filter(Boolean);
  const transliteratedValues = values
    .flatMap((value) => [
      transliterateForLanguage(value, "ar"),
      transliterateForLanguage(value, "ti"),
      transliterateForLanguage(value, "so"),
    ])
    .map((value) => normalizeSearchText(value))
    .filter(Boolean);
  const aliases = values.flatMap(getAliasVariants);

  return [...normalizedValues, ...transliteratedValues, ...aliases].join(" ");
}

export function normalizeSearchQuery(value) {
  const rawValue = String(value || "").trim();

  if (!rawValue) {
    return "";
  }

  const hasNonAsciiCharacters = Array.from(rawValue).some(
    (character) => character.charCodeAt(0) > 127,
  );

  if (hasNonAsciiCharacters) {
    return "";
  }

  const normalizedValue = normalizeSearchText(rawValue);
  return SEARCH_ALIAS_LOOKUP.get(normalizedValue) || normalizedValue;
}

export function matchesLocalizedSearch(parts, term) {
  const normalizedTerm = normalizeSearchQuery(term);

  if (!normalizedTerm) {
    return true;
  }

  const haystack = buildSearchHaystack(parts);

  if (haystack.includes(normalizedTerm)) {
    return true;
  }

  const haystackTokens = haystack.split(/\s+/).filter(Boolean);
  const termTokens = normalizedTerm.split(/\s+/).filter(Boolean);

  return termTokens.every((token) =>
    haystackTokens.some(
      (candidate) =>
        candidate.includes(token) ||
        token.includes(candidate) ||
        similarityScore(candidate, token) >= 0.88,
    ),
  );
}

export function multilineToList(value) {
  return String(value || "")
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export function listToMultiline(list) {
  return Array.isArray(list) ? list.filter(Boolean).join("\n") : "";
}

export function getPropertyUtilityState(utilities) {
  const source = utilities && typeof utilities === "object" ? utilities : {};

  return UTILITY_OPTIONS.reduce((accumulator, option) => {
    accumulator[option.name] = Boolean(source[option.name]);
    return accumulator;
  }, {});
}

export function getPropertyRoomFeatureState(roomFeatures) {
  const source =
    roomFeatures && typeof roomFeatures === "object" ? roomFeatures : {};

  return ROOM_FEATURE_OPTIONS.reduce((accumulator, option) => {
    accumulator[option.name] = Boolean(source[option.name]);
    return accumulator;
  }, {});
}

export function normalizeProperty(property) {
  const normalized = property || {};
  const price = Number(normalized.price);
  const rooms = Number(normalized.rooms);
  const bathrooms = Number(normalized.bathrooms);
  const storeAreaSquareMeters = Number(normalized.storeAreaSquareMeters);
  const storeCubicMeters = Number(normalized.storeCubicMeters);
  const owner =
    normalized.owner && typeof normalized.owner === "object"
      ? normalized.owner
      : {};

  return {
    ...normalized,
    title: normalized.title?.trim() || "Untitled property",
    city: normalized.city?.trim() || "",
    location: normalized.location?.trim() || "Location unavailable",
    locationDetails: normalized.locationDetails?.trim() || "",
    description: normalized.description?.trim() || "",
    price: Number.isFinite(price) ? price : 0,
    currency: normalized.currency || "USD",
    rooms: Number.isFinite(rooms) && rooms > 0 ? rooms : null,
    bathrooms: Number.isFinite(bathrooms) && bathrooms > 0 ? bathrooms : null,
    spaceSize: normalized.spaceSize?.trim() || "",
    storeAreaSquareMeters: Number.isFinite(storeAreaSquareMeters)
      ? storeAreaSquareMeters
      : null,
    storeCubicMeters: Number.isFinite(storeCubicMeters)
      ? storeCubicMeters
      : null,
    listingType: canonicalizeListingTypeValue(normalized.listingType || ""),
    type: canonicalizePropertyTypeValue(normalized.type || ""),
    status: normalized.status || "pending",
    agentName: normalized.agentName?.trim() || "Junub Homes",
    agentPhone: normalized.agentPhone?.trim() || "",
    agentEmail: normalized.agentEmail?.trim() || "",
    images: Array.isArray(normalized.images)
      ? normalized.images.map((image) => getImageUrl(image))
      : [],
    rules: Array.isArray(normalized.rules)
      ? normalized.rules.map((rule) => rule?.trim()).filter(Boolean)
      : [],
    utilities: getPropertyUtilityState(normalized.utilities),
    roomFeatures: getPropertyRoomFeatureState(normalized.roomFeatures),
    owner: {
      name: owner.name?.trim() || "",
      phone: owner.phone?.trim() || "",
      email: owner.email?.trim() || "",
      propertyId: owner.propertyId?.trim() || normalized.id || "",
    },
  };
}

export function getResponsibleContactDetails(property) {
  const normalizedProperty = normalizeProperty(property);
  const hasOwnerDetails = Boolean(
    normalizedProperty.owner?.name ||
    normalizedProperty.owner?.phone ||
    normalizedProperty.owner?.email,
  );

  if (hasOwnerDetails) {
    return {
      type: "owner",
      name: normalizedProperty.owner.name,
      phone: normalizedProperty.owner.phone,
      email: normalizedProperty.owner.email,
      propertyId:
        normalizedProperty.owner.propertyId || normalizedProperty.id || "",
    };
  }

  return {
    type: "agent",
    name: normalizedProperty.agentName || "Junub Homes",
    phone: normalizedProperty.agentPhone || "",
    email: normalizedProperty.agentEmail || "",
    propertyId: normalizedProperty.id || "",
  };
}

export function normalizeUser(user) {
  const normalized = user || {};
  const inferredStatus = normalized.approved ? "active" : "pending";

  return {
    ...normalized,
    name: normalized.name?.trim() || "Unnamed user",
    email: normalized.email?.trim().toLowerCase() || "",
    phone: normalized.phone?.trim() || "",
    role: normalizeRole(normalized.role),
    approved: normalized.approved === true,
    accountStatus: String(normalized.accountStatus || inferredStatus)
      .trim()
      .toLowerCase(),
  };
}

export function normalizeVisitorMessage(message) {
  const normalized = message || {};

  return {
    ...normalized,
    name: normalized.name?.trim() || "Unknown visitor",
    phone: normalized.phone?.trim() || "",
    subject: normalized.subject?.trim() || "General inquiry",
    message: normalized.message?.trim() || "",
    status: normalized.status || "new",
  };
}

export function normalizePropertyContactRequest(request) {
  const normalized = request || {};
  const owner =
    normalized.owner && typeof normalized.owner === "object"
      ? normalized.owner
      : {};
  const agent =
    normalized.agent && typeof normalized.agent === "object"
      ? normalized.agent
      : {};

  return {
    ...normalized,
    propertyId: normalized.propertyId?.trim() || "",
    propertyTitle: normalized.propertyTitle?.trim() || "Untitled property",
    propertyLocation:
      normalized.propertyLocation?.trim() || "Location unavailable",
    propertyLocationDetails: normalized.propertyLocationDetails?.trim() || "",
    propertyPrice: Number.isFinite(Number(normalized.propertyPrice))
      ? Number(normalized.propertyPrice)
      : 0,
    propertyImage: getImageUrl(normalized.propertyImage),
    contactType: normalized.contactType === "owner" ? "owner" : "agent",
    contactName: normalized.contactName?.trim() || "",
    contactPhone: normalized.contactPhone?.trim() || "",
    contactEmail: normalized.contactEmail?.trim().toLowerCase() || "",
    ownerName: normalized.ownerName?.trim() || owner.name?.trim() || "",
    ownerPhone: normalized.ownerPhone?.trim() || owner.phone?.trim() || "",
    ownerEmail:
      normalized.ownerEmail?.trim().toLowerCase() ||
      owner.email?.trim().toLowerCase() ||
      "",
    agentName: normalized.agentName?.trim() || agent.name?.trim() || "",
    agentPhone: normalized.agentPhone?.trim() || agent.phone?.trim() || "",
    agentEmail:
      normalized.agentEmail?.trim().toLowerCase() ||
      agent.email?.trim().toLowerCase() ||
      "",
    requesterId: normalized.requesterId?.trim() || "",
    requesterEmail: normalized.requesterEmail?.trim().toLowerCase() || "",
    status: normalized.status || "new",
  };
}

export function normalizeAdvert(advert) {
  const normalized = advert || {};
  const legacyMediaUrl = normalized.mediaUrl?.trim() || "";
  const legacyStoragePath = normalized.storagePath?.trim() || "";
  const legacyMediaType = normalized.mediaType || "image";
  const mediaItems = Array.isArray(normalized.mediaItems)
    ? normalized.mediaItems
        .map((item) => ({
          mediaUrl: item?.mediaUrl?.trim() || "",
          storagePath: item?.storagePath?.trim() || "",
          mediaType: item?.mediaType || "image",
        }))
        .filter((item) => item.mediaUrl)
    : [];
  const normalizedMediaItems =
    mediaItems.length > 0 || !legacyMediaUrl
      ? mediaItems
      : [
          {
            mediaUrl: legacyMediaUrl,
            storagePath: legacyStoragePath,
            mediaType: legacyMediaType,
          },
        ];
  const primaryMedia = normalizedMediaItems[0] || {
    mediaUrl: legacyMediaUrl,
    storagePath: legacyStoragePath,
    mediaType: legacyMediaType,
  };

  return {
    ...normalized,
    title: normalized.title?.trim() || "Untitled advert",
    description: normalized.description?.trim() || "",
    mediaUrl: primaryMedia.mediaUrl || "",
    mediaType: primaryMedia.mediaType || "image",
    linkUrl: normalized.linkUrl?.trim() || "",
    isActive: normalized.isActive !== false,
    storagePath: primaryMedia.storagePath || "",
    mediaItems: normalizedMediaItems,
  };
}

export function normalizeHotelServiceRequest(request) {
  const normalized = request || {};

  return {
    ...normalized,
    preferredHotelId: normalized.preferredHotelId?.trim() || "",
    preferredHotelName: normalized.preferredHotelName?.trim() || "",
    contactName: normalized.contactName?.trim() || "Unknown requester",
    organizationName: normalized.organizationName?.trim() || "",
    organizationType: normalized.organizationType?.trim() || "",
    contactEmail: normalized.contactEmail?.trim().toLowerCase() || "",
    contactPhone: normalized.contactPhone?.trim() || "",
    whatsapp: normalized.whatsapp?.trim() || "",
    requestType: normalized.requestType?.trim() || "",
    stayFocus: normalized.stayFocus?.trim() || "",
    destination: normalized.destination?.trim() || "Juba, South Sudan",
    checkInDate: normalized.checkInDate?.trim() || "",
    checkOutDate: normalized.checkOutDate?.trim() || "",
    roomsNeeded: Number.isFinite(Number(normalized.roomsNeeded))
      ? Number(normalized.roomsNeeded)
      : 0,
    guestsCount: Number.isFinite(Number(normalized.guestsCount))
      ? Number(normalized.guestsCount)
      : 0,
    budgetMin: Number.isFinite(Number(normalized.budgetMin))
      ? Number(normalized.budgetMin)
      : 0,
    budgetMax: Number.isFinite(Number(normalized.budgetMax))
      ? Number(normalized.budgetMax)
      : 0,
    paymentPreference: normalized.paymentPreference?.trim() || "",
    securityPriority: normalized.securityPriority?.trim() || "",
    airportPickup: normalized.airportPickup === true,
    simCards: normalized.simCards === true,
    driverService: normalized.driverService === true,
    translatorService: normalized.translatorService === true,
    medicalSupport: normalized.medicalSupport === true,
    notes: normalized.notes?.trim() || "",
    status: normalized.status || "new",
  };
}

export function formatCarPrice(car, locale = "en") {
  const value = Number(car?.price);
  const currency = car?.currency || "USD";
  const suffix =
    car?.saleType === "For Rent"
      ? getBillingSuffix("Short Stay", locale)
      : "";

  if (!hasDisplayPrice(value)) {
    return getMissingPriceLabel(locale);
  }

  return `${formatPrice(value, currency, locale)}${suffix}`;
}

function splitLegacyBrandModel(value) {
  const combined = String(value || "").trim();

  if (!combined) {
    return {
      brand: "",
      model: "",
    };
  }

  const [brand = "", ...modelParts] = combined.split(/\s+/);

  return {
    brand,
    model: modelParts.join(" ").trim(),
  };
}

export function buildCarBrandModel(brand, model, fallback = "") {
  const normalizedBrand = String(brand || "").trim();
  const normalizedModel = String(model || "").trim();
  const combined = [normalizedBrand, normalizedModel].filter(Boolean).join(" ").trim();

  return combined || String(fallback || "").trim();
}

export function normalizeCar(car) {
  const normalized = car || {};
  const year = Number(normalized.year);
  const price = Number(normalized.price);
  const mileage = Number(normalized.mileage);
  const legacyName = normalized.brandModel?.trim() || "";
  const legacyParts = splitLegacyBrandModel(legacyName);
  const brand = normalized.brand?.trim() || legacyParts.brand;
  const model = normalized.model?.trim() || legacyParts.model;
  const brandModel = buildCarBrandModel(brand, model, legacyName);

  return {
    ...normalized,
    brand,
    model,
    brandModel: brandModel || "Untitled car",
    year: Number.isFinite(year) ? year : null,
    bodyType: normalized.bodyType?.trim() || "",
    transmission: normalized.transmission?.trim() || "",
    fuelType: normalized.fuelType?.trim() || "",
    engineSize: normalized.engineSize?.trim() || "",
    mileage: Number.isFinite(mileage) ? mileage : null,
    driveType: normalized.driveType?.trim() || "",
    color: normalized.color?.trim() || "",
    mechanicalCondition: normalized.mechanicalCondition?.trim() || "",
    interiorCondition: normalized.interiorCondition?.trim() || "",
    exteriorCondition: normalized.exteriorCondition?.trim() || "",
    accidentHistory: normalized.accidentHistory?.trim() || "",
    registrationStatus: normalized.registrationStatus?.trim() || "",
    serviceHistory: normalized.serviceHistory?.trim() || "",
    features: Array.isArray(normalized.features)
      ? normalized.features.map((feature) => feature?.trim()).filter(Boolean)
      : [],
    price: Number.isFinite(price) ? price : 0,
    currency: normalized.currency || "USD",
    negotiable: normalized.negotiable === true,
    phone: normalized.phone?.trim() || "",
    whatsapp: normalized.whatsapp?.trim() || "",
    location: normalized.location?.trim() || "Juba, South Sudan",
    images: Array.isArray(normalized.images)
      ? normalized.images.map((image) => getImageUrl(image))
      : [],
    videoUrl: normalized.videoUrl?.trim() || "",
    status: normalized.status || "pending",
    listingType: "car",
    saleType: canonicalizeListingTypeValue(normalized.saleType || "For Sale"),
    agentId: normalized.agentId?.trim?.() || normalized.agentId || "",
    agentName: normalized.agentName?.trim() || "",
    agentPhone: normalized.agentPhone?.trim() || "",
    agentEmail: normalized.agentEmail?.trim().toLowerCase() || "",
    isFeatured: normalized.isFeatured === true,
  };
}

export function carMatchesFilters(car, filters = {}) {
  const normalizedCar = normalizeCar(car);
  if (
    !matchesLocalizedSearch(
      [
      normalizedCar.brandModel,
      normalizedCar.brand,
      normalizedCar.model,
      normalizedCar.bodyType,
      normalizedCar.location,
      normalizedCar.color,
      normalizedCar.fuelType,
      normalizedCar.saleType,
      normalizedCar.transmission,
    ],
      filters.search,
    )
  ) {
    return false;
  }

  if (filters.saleType && normalizedCar.saleType !== filters.saleType) {
    return false;
  }

  if (filters.bodyType && normalizedCar.bodyType !== filters.bodyType) {
    return false;
  }

  if (filters.transmission && normalizedCar.transmission !== filters.transmission) {
    return false;
  }

  if (filters.fuelType && normalizedCar.fuelType !== filters.fuelType) {
    return false;
  }

  if (
    filters.maxPrice &&
    normalizedCar.price > parseLocalizedNumber(filters.maxPrice)
  ) {
    return false;
  }

  return true;
}

export function propertyMatchesSearch(property, term) {
  if (!term) {
    return true;
  }

  return matchesLocalizedSearch(
    [
      property.title,
      property.location,
      property.locationDetails,
      property.description,
      property.agentName,
      property.owner?.name,
      property.type,
      property.listingType,
    ],
    term,
  );
}

export function getListingTypeFromPath(pathname) {
  switch (pathname) {
    case "/for-sale":
      return "For Sale";
    case "/for-rent":
      return "For Rent";
    case "/for-lease":
      return "For Lease";
    case "/short-stay":
      return "Short Stay";
    default:
      return "";
  }
}
